import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { get, authPost, authPut, authDelete } from '../../services/apiService';
import { isAdmin, isAuthenticated } from '../../services/authService';
import { format } from 'date-fns';

const darkGreenColor = "#1D4434";

/**
 * HIPAA/CUI Services - Shows services tracking for HIPAA/CUI compliance
 * @returns {JSX.Element} HipaaCuiServices component
 */
const HipaaCuiServices = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [serviceFilters, setServiceFilters] = useState({
    inScope: ''
  });
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortField, setSortField] = useState(null);
  
  // Modal state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    serviceName: '',
    gitRepoUrl: '',
    inScope: 'In-Scope',
    comments: ''
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  const loadData = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      
      const response = await get('/api/hipaa-cui/services');
      
      if (response.success) {
        // Convert any boolean inScope values to string format for backward compatibility
        const data = response.data || [];
        const convertedServices = data.length > 0 && typeof data[0].inScope === 'boolean'
          ? data.map(svc => ({
              ...svc,
              inScope: svc.inScope ? 'In-Scope' : 'Out-of-Scope'
            }))
          : data;
        setServices(convertedServices);
      }
    } catch (err) {
      console.error('Error loading HIPAA/CUI services:', err);
      setError(err.message || 'Failed to load services data');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const applyServiceFilters = useCallback(() => {
    let filtered = [...services];
    
    if (serviceFilters.inScope) {
      filtered = filtered.filter(s => s.inScope === serviceFilters.inScope);
    }
    
    // Apply sorting
    if (sortField) {
      filtered.sort((a, b) => {
        let aValue = a[sortField] || '';
        let bValue = b[sortField] || '';
        
        // For inScope, normalize values for proper sorting
        if (sortField === 'inScope') {
          // Sort order: In-Scope, In Scope When available, Out-of-Scope
          const order = { 'In-Scope': 1, 'In Scope When available': 2, 'Out-of-Scope': 3 };
          aValue = order[aValue] || 999;
          bValue = order[bValue] || 999;
        } else {
          // String comparison for other fields
          aValue = String(aValue).toLowerCase();
          bValue = String(bValue).toLowerCase();
        }
        
        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    setFilteredServices(filtered);
  }, [services, serviceFilters, sortField, sortOrder]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyServiceFilters();
  }, [applyServiceFilters]);

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field and default to ascending
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleOpenDialog = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        serviceName: service.serviceName || '',
        gitRepoUrl: service.gitRepoUrl || '',
        inScope: service.inScope || 'In-Scope',
        comments: service.comments || ''
      });
    } else {
      setEditingService(null);
      setFormData({
        serviceName: '',
        gitRepoUrl: '',
        inScope: 'In-Scope',
        comments: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingService(null);
    setFormData({
      serviceName: '',
      gitRepoUrl: '',
      inScope: 'In-Scope',
      comments: ''
    });
  };

  const handleSaveService = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!formData.serviceName.trim()) {
        setError('Service Name is required');
        setSaving(false);
        return;
      }

      let response;
      if (editingService) {
        response = await authPut(`/api/hipaa-cui/services/${editingService._id}`, formData);
      } else {
        response = await authPost('/api/hipaa-cui/services', formData);
      }

      if (response.success) {
        handleCloseDialog();
        await loadData(false); // Reload services without showing loading spinner
      } else {
        setError(response.message || 'Failed to save service');
      }
    } catch (err) {
      console.error('Error saving service:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save service');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      setDeleting(id);
      setError(null);

      const response = await authDelete(`/api/hipaa-cui/services/${id}`);

      if (response.success) {
        await loadData(false); // Reload services without showing loading spinner
      } else {
        setError(response.message || 'Failed to delete service');
      }
    } catch (err) {
      console.error('Error deleting service:', err);
      setError(err.response?.data?.message || err.message || 'Failed to delete service');
    } finally {
      setDeleting(null);
    }
  };

  const handleDownloadJson = () => {
    try {
      // Filter out internal fields before exporting
      const cleanedServices = services.map(({ _id, __v, createdAt, updatedAt, createdBy, updatedBy, ...rest }) => rest);
      const dataStr = JSON.stringify(cleanedServices, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hipaa-cui-services-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading JSON:', err);
      setError('Failed to download JSON file');
    }
  };

  const handleDownloadReport = () => {
    try {
      if (filteredServices.length === 0) {
        setError('No services to export');
        return;
      }

      const printWindow = window.open('', '_blank');
      const printDate = new Date();
      const formattedDate = format(printDate, 'MMMM dd, yyyy');
      const formattedTime = format(printDate, 'hh:mm a');
      
      // Calculate statistics
      const totalServices = filteredServices.length;
      const inScopeCount = filteredServices.filter(s => s.inScope === 'In-Scope').length;
      const outOfScopeCount = filteredServices.filter(s => s.inScope === 'Out-of-Scope').length;
      const inScopeRate = totalServices > 0 ? ((inScopeCount / totalServices) * 100).toFixed(1) : 0;
      
      const escapeHtml = (text) => {
        if (!text) return '';
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
      };
      
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>HIPAA/CUI Services Report</title>
          <meta charset="UTF-8">
          <style>
            @page {
              margin: 2cm 1.5cm;
              size: A4;
            }
            @media print {
              body {
                margin: 0;
                padding: 0;
              }
              .page-break {
                page-break-before: always;
              }
              thead {
                display: table-header-group;
              }
              tfoot {
                display: table-footer-group;
              }
              tr {
                page-break-inside: avoid;
              }
            }
            * {
              box-sizing: border-box;
            }
            body {
              font-family: 'Calibri', 'Arial', 'Helvetica', sans-serif;
              font-size: 11pt;
              color: #2c3e50;
              line-height: 1.5;
              margin: 0;
              padding: 0;
              background: #ffffff;
            }
            .header {
              background: linear-gradient(135deg, #1D4434 0%, #2d5a47 100%);
              color: white;
              padding: 25px 30px;
              margin-bottom: 30px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            }
            .header-content {
              display: flex;
              justify-content: space-between;
              align-items: center;
              flex-wrap: wrap;
            }
            .header-title {
              font-size: 24pt;
              font-weight: 700;
              margin: 0 0 8px 0;
              letter-spacing: 0.5px;
            }
            .header-subtitle {
              font-size: 11pt;
              opacity: 0.9;
              margin: 0;
            }
            .header-meta {
              text-align: right;
              font-size: 10pt;
              opacity: 0.9;
            }
            .executive-summary {
              background: #f8f9fa;
              border: 2px solid #1D4434;
              border-radius: 8px;
              padding: 25px;
              margin-bottom: 30px;
              page-break-inside: avoid;
            }
            .executive-summary h2 {
              color: #1D4434;
              font-size: 18pt;
              font-weight: 700;
              margin: 0 0 20px 0;
              padding-bottom: 10px;
              border-bottom: 2px solid #1D4434;
            }
            .stats-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 15px;
              margin-bottom: 20px;
            }
            .stat-card {
              background: white;
              border-left: 4px solid #1D4434;
              padding: 15px;
              border-radius: 4px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.05);
            }
            .stat-label {
              font-size: 10pt;
              color: #666;
              margin-bottom: 8px;
              font-weight: 500;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .stat-value {
              font-size: 24pt;
              font-weight: 700;
              color: #1D4434;
            }
            .stat-card.success .stat-value {
              color: #2e7d32;
            }
            .stat-card.info .stat-value {
              color: #0288d1;
            }
            .progress-section {
              margin-top: 20px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
            }
            .progress-bar-container {
              background: #e0e0e0;
              border-radius: 10px;
              height: 25px;
              margin: 10px 0;
              overflow: hidden;
            }
            .progress-bar {
              background: linear-gradient(90deg, #2e7d32 0%, #4caf50 100%);
              height: 100%;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: 600;
              font-size: 10pt;
              transition: width 0.3s ease;
            }
            .section-title {
              font-size: 16pt;
              font-weight: 700;
              color: #1D4434;
              margin: 30px 0 15px 0;
              padding-bottom: 8px;
              border-bottom: 2px solid #1D4434;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
              background: white;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
              page-break-inside: auto;
            }
            thead {
              background: linear-gradient(135deg, #1D4434 0%, #2d5a47 100%);
              color: white;
            }
            th {
              padding: 14px 10px;
              text-align: left;
              font-weight: 600;
              font-size: 10pt;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              border: 1px solid rgba(255,255,255,0.2);
            }
            td {
              padding: 12px 10px;
              border: 1px solid #e0e0e0;
              vertical-align: top;
              word-wrap: break-word;
              font-size: 10pt;
            }
            tbody tr {
              transition: background-color 0.2s;
            }
            tbody tr:nth-child(even) {
              background-color: #f8f9fa;
            }
            tbody tr:hover {
              background-color: #e8f5e9;
            }
            .badge {
              display: inline-block;
              padding: 5px 12px;
              border-radius: 15px;
              font-size: 9pt;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              white-space: nowrap;
            }
            .badge-success { 
              background-color: #2e7d32; 
              color: white; 
            }
            .badge-default { 
              background-color: #757575; 
              color: white; 
            }
            .badge-warning { 
              background-color: #ed6c02; 
              color: white; 
            }
            .comment-cell {
              max-width: 300px;
              word-wrap: break-word;
              white-space: pre-wrap;
              line-height: 1.4;
            }
            .title-cell {
              font-weight: 600;
              color: #1D4434;
            }
            .url-cell {
              max-width: 250px;
              word-wrap: break-word;
            }
            a {
              color: #1976d2;
              text-decoration: none;
            }
            a:hover {
              text-decoration: underline;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px solid #1D4434;
              text-align: center;
              font-size: 9pt;
              color: #666;
            }
            .text-center {
              text-align: center;
            }
            .text-right {
              text-align: right;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="header-content">
              <div>
                <div class="header-title">HIPAA/CUI Services Report</div>
                <div class="header-subtitle">Compliance Management & Services Tracking</div>
              </div>
              <div class="header-meta">
                <div><strong>Report Date:</strong> ${formattedDate}</div>
                <div><strong>Generated:</strong> ${formattedTime}</div>
              </div>
            </div>
          </div>

          <!-- Executive Summary -->
          <div class="executive-summary">
            <h2>Executive Summary</h2>
            <div class="stats-grid">
              <div class="stat-card">
                <div class="stat-label">Total Services</div>
                <div class="stat-value">${totalServices}</div>
              </div>
              <div class="stat-card success">
                <div class="stat-label">In-Scope</div>
                <div class="stat-value">${inScopeCount}</div>
              </div>
              <div class="stat-card info">
                <div class="stat-label">Out-of-Scope</div>
                <div class="stat-value">${outOfScopeCount}</div>
              </div>
            </div>
            <div class="progress-section">
              <div style="display: flex; justify-content: space-between; margin-bottom: 5px;">
                <span style="font-weight: 600; color: #1D4434;">In-Scope Rate</span>
                <span style="font-weight: 600; color: #1D4434;">${inScopeRate}%</span>
              </div>
              <div class="progress-bar-container">
                <div class="progress-bar" style="width: ${inScopeRate}%;">${inScopeRate}%</div>
              </div>
            </div>
          </div>

          <!-- Detailed Services List -->
          <div class="section-title">Services Details</div>
          
          <table>
            <thead>
              <tr>
                <th style="width: 5%;" class="text-center">#</th>
                <th style="width: 20%;">Service Name</th>
                <th style="width: 25%;">Git Repo URL</th>
                <th style="width: 15%;">In-Scope/Out-of-Scope</th>
                <th style="width: 20%;">Comments</th>
                <th style="width: 8%;">Last Changed By</th>
                <th style="width: 7%;">Changed Date</th>
              </tr>
            </thead>
            <tbody>
      `;
      
      filteredServices.forEach((service, index) => {
        const scopeClass = 
          service.inScope === 'In-Scope' ? 'badge-success' :
          service.inScope === 'Out-of-Scope' ? 'badge-default' : 'badge-warning';
        
        htmlContent += `
          <tr>
            <td class="text-center">${index + 1}</td>
            <td class="title-cell">${escapeHtml(service.serviceName || '-')}</td>
            <td class="url-cell">${service.gitRepoUrl ? `<a href="${escapeHtml(service.gitRepoUrl)}" target="_blank">${escapeHtml(service.gitRepoUrl)}</a>` : '-'}</td>
            <td><span class="badge ${scopeClass}">${escapeHtml(service.inScope || 'In-Scope')}</span></td>
            <td class="comment-cell">${escapeHtml(service.comments || '-')}</td>
            <td>${escapeHtml(service.updatedBy?.username || '-')}</td>
            <td>${service.updatedAt ? format(new Date(service.updatedAt), 'MMM dd, yyyy HH:mm') : '-'}</td>
          </tr>
        `;
      });
      
      htmlContent += `
            </tbody>
          </table>
          
          <div class="footer">
            <div><strong>HIPAA/CUI Services Report</strong></div>
            <div style="margin-top: 5px;">Generated on ${formattedDate} at ${formattedTime}</div>
            <div style="margin-top: 5px;">This report contains ${totalServices} service${totalServices !== 1 ? 's' : ''} | Confidential Document</div>
          </div>
        </body>
        </html>
      `;
      
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Wait for content to load, then trigger print dialog
      setTimeout(() => {
        printWindow.print();
      }, 250);

      setSuccessMessage('Services report opened for printing/download as PDF');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('Error generating services report:', err);
      setError('Failed to generate services report');
    }
  };

  const escapeHtml = (text) => {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  const handleUploadJson = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      setError('Please upload a JSON file');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccessMessage(null);

      const fileContent = await file.text();
      const jsonData = JSON.parse(fileContent);

      if (!Array.isArray(jsonData)) {
        setError('JSON file must contain an array of service objects');
        return;
      }

      const response = await authPost('/api/hipaa-cui/services/bulk-import', {
        services: jsonData,
        clearExisting: false
      });

      if (response.success) {
        const summary = response.summary || {};
        const message = [
          response.message,
          summary.errors && summary.errors.length > 0 && `${summary.errors.length} error(s) occurred`
        ].filter(Boolean).join('. ');
        
        setSuccessMessage(message);
        if (summary.errors && summary.errors.length > 0) {
          setError(`Errors: ${summary.errors.slice(0, 5).join('; ')}${summary.errors.length > 5 ? ' ...' : ''}`);
        }
        await loadData(false);
        setTimeout(() => {
          setSuccessMessage(null);
          setError(null);
        }, 10000);
      } else {
        setError(response.message || 'Failed to import services');
      }
    } catch (err) {
      console.error('Error uploading JSON:', err);
      setError(err.response?.data?.message || err.message || 'Failed to upload JSON file. Please check the file format.');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Filters and Actions in Same Line */}
      <Paper sx={{ p: 1.5, mb: 1.5, borderRadius: 1 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Scope</InputLabel>
              <Select
                value={serviceFilters.inScope}
                onChange={(e) => setServiceFilters({ ...serviceFilters, inScope: e.target.value })}
                label="Scope"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="In-Scope">In-Scope</MenuItem>
                <MenuItem value="Out-of-Scope">Out-of-Scope</MenuItem>
                <MenuItem value="In Scope When available">In Scope When available</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {isAuthenticated() && (
            <Grid item xs={12} sm={8} md={9}>
              <Box display="flex" gap={0.75} flexWrap="wrap" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                  sx={{
                    backgroundColor: darkGreenColor,
                    '&:hover': { backgroundColor: '#0f2e1f' }
                  }}
                >
                  Add
                </Button>
                {isAdmin() && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={uploading ? <CircularProgress size={14} /> : <UploadIcon />}
                    component="label"
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Upload'}
                    <input
                      type="file"
                      accept=".json,application/json"
                      hidden
                      onChange={handleUploadJson}
                      disabled={uploading}
                    />
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadJson}
                  disabled={services.length === 0}
                >
                  Download
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadReport}
                  disabled={filteredServices.length === 0}
                >
                  Download Report
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Services Table - Full Height */}
      {loading ? (
        <Paper sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, flex: 1 }}>
          <Box textAlign="center">
            <CircularProgress size={50} sx={{ mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Loading services...
            </Typography>
          </Box>
        </Paper>
      ) : (
        <TableContainer 
          component={Paper} 
          elevation={0}
          sx={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            '& .MuiTable-root': {
              minWidth: 650
            }
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2,
                    '&:first-of-type': {
                      borderTopLeftRadius: '8px'
                    },
                    '&:last-of-type': {
                      borderTopRightRadius: '8px'
                    }
                  }}
                  width="80"
                >
                  Sr. No
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2
                  }}
                >
                  Service Name
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2
                  }}
                >
                  Git Repo URL
                </TableCell>
                <TableCell
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2,
                    '& .MuiTableSortLabel-root': {
                      color: 'white',
                      '&:hover': {
                        color: 'rgba(255, 255, 255, 0.8)'
                      },
                      '& .MuiTableSortLabel-icon': {
                        color: 'rgba(255, 255, 255, 0.7) !important'
                      }
                    }
                  }}
                >
                  <TableSortLabel
                    active={sortField === 'inScope'}
                    direction={sortField === 'inScope' ? sortOrder : 'asc'}
                    onClick={() => handleSort('inScope')}
                    sx={{ color: 'white' }}
                  >
                    In-Scope/Out-of-Scope
                  </TableSortLabel>
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2
                  }}
                >
                  Comments
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2
                  }}
                >
                  Last Changed By
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: 'background.paper',
                    background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    borderBottom: 'none',
                    py: 2
                  }}
                >
                  Changed Date
                </TableCell>
                {isAuthenticated() && (
                  <TableCell 
                    align="right"
                    sx={{ 
                      backgroundColor: 'background.paper',
                      background: 'linear-gradient(135deg, #1D4434 0%, #2d5a47 100%)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      borderBottom: 'none',
                      py: 2
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredServices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAuthenticated() ? 8 : 7} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                      No services found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredServices.map((service, index) => (
                  <TableRow 
                    key={service._id}
                    sx={{
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'rgba(29, 68, 52, 0.04)',
                        transform: 'scale(1.001)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)'
                      },
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500, color: 'text.secondary', py: 2 }}>
                      {index + 1}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: darkGreenColor }}>
                        {service.serviceName}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {service.gitRepoUrl ? (
                        <Typography
                          component="a"
                          href={service.gitRepoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          sx={{ 
                            color: 'primary.main', 
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline',
                              color: 'primary.dark'
                            },
                            display: 'block',
                            maxWidth: '300px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {service.gitRepoUrl}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip 
                        label={service.inScope || 'In-Scope'}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          height: '28px',
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}
                        color={
                          service.inScope === 'In-Scope' ? 'success' :
                          service.inScope === 'Out-of-Scope' ? 'default' : 'warning'
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2, maxWidth: 300 }}>
                      <Tooltip title={service.comments || '-'} arrow>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: 'text.secondary'
                          }}
                        >
                          {service.comments || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {service.updatedBy?.username || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {service.updatedAt ? format(new Date(service.updatedAt), 'MMM dd, yyyy HH:mm') : '-'}
                      </Typography>
                    </TableCell>
                    {isAuthenticated() && (
                      <TableCell align="right" sx={{ py: 2 }}>
                        <Box display="flex" gap={0.5} justifyContent="flex-end">
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(service)}
                              sx={{
                                color: 'primary.main',
                                '&:hover': {
                                  backgroundColor: 'primary.light',
                                  color: 'primary.dark',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteService(service._id)}
                              disabled={deleting === service._id}
                              sx={{
                                color: 'error.main',
                                '&:hover': {
                                  backgroundColor: 'error.light',
                                  color: 'error.dark',
                                  transform: 'scale(1.1)'
                                },
                                '&:disabled': {
                                  opacity: 0.5
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              {deleting === service._id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingService ? 'Edit Service' : 'Add Service'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Service Name"
                  value={formData.serviceName}
                  onChange={(e) => setFormData({ ...formData, serviceName: e.target.value })}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Git Repo URL"
                  value={formData.gitRepoUrl}
                  onChange={(e) => setFormData({ ...formData, gitRepoUrl: e.target.value })}
                  placeholder="https://github.com/..."
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>In-Scope/Out-of-Scope</InputLabel>
                  <Select
                    value={formData.inScope}
                    onChange={(e) => setFormData({ ...formData, inScope: e.target.value })}
                    label="In-Scope/Out-of-Scope"
                  >
                    <MenuItem value="In-Scope">In-Scope</MenuItem>
                    <MenuItem value="Out-of-Scope">Out-of-Scope</MenuItem>
                    <MenuItem value="In Scope When available">In Scope When available</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Comments"
                  value={formData.comments}
                  onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                  multiline
                  rows={4}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveService}
            variant="contained"
            disabled={saving || !formData.serviceName.trim()}
            sx={{
              backgroundColor: darkGreenColor,
              '&:hover': { backgroundColor: '#0f2e1f' }
            }}
          >
            {saving ? 'Saving...' : editingService ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HipaaCuiServices;

